import React, { useEffect, useState } from 'react';
import { orderAPI } from '../../services/api';
import { Loader } from 'lucide-react';

const AdminOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            console.log("Fetching orders for Admin...");
            const response = await orderAPI.getAllOrders();
            console.log("Admin Orders Response:", response.data);

            // Handle both { orders: [...] } and direct array (just in case)
            const ordersData = response.data.orders || response.data || [];
            console.log("Parsed Orders Data:", ordersData);

            setOrders(ordersData);
        } catch (error) {
            console.error("Failed to fetch orders", error);
            // Verify if error is 403 (Permissions)
            if (error.response && error.response.status === 403) {
                alert("Access Denied: You are not an Admin.");
            }
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (id, status) => {
        try {
            // Simple status update
            const payload = { status };

            await orderAPI.updateOrderStatus(id, payload);
            // Optimistic update
            setOrders(orders.map(o => o.id === id ? { ...o, status } : o));
        } catch (error) {
            console.error("Failed to update status", error);
            // show specific validation errors returned by backend
            const err = error.response?.data;
            if (err?.errors) {
                alert('Validation error: ' + JSON.stringify(err.errors));
            } else if (err?.message) {
                alert('Error: ' + err.message);
            } else {
                alert("Failed to update status");
            }
        }
    };

    return (
        <div className="pt-24 min-h-screen bg-gray-100 px-6 pb-12">
            <div className="container mx-auto">
                <h1 className="text-3xl font-bold text-gray-800 mb-8">Order Management</h1>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <Loader className="animate-spin text-green-600" size={48} />
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-md overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Status / Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {orders.map((order) => (
                                    <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            #{order.id}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="font-medium text-gray-900">{order.user ? order.user.name : 'Unknown'}</div>
                                            <div className="text-gray-500">{order.user ? order.user.email : 'No Email'}</div>
                                            <div className="text-xs text-gray-400 mt-1">
                                                {order.address?.mobile}<br />
                                                {order.address?.address}, {order.address?.city} - {order.address?.pincode}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            <ul className="list-disc list-inside">
                                                {order.items && order.items.map((item, index) => (
                                                    <li key={index}>
                                                        <span className="font-medium text-gray-900">
                                                            {item.variation?.product?.name || item.product?.name || 'Unknown Product'}
                                                        </span>
                                                        <span className="text-gray-500"> x {item.qty || item.quantity}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            ₹{order.totalAmount}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(order.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <select
                                                value={order.status}
                                                onChange={(e) => updateStatus(order.id, e.target.value)}
                                                className={`text-xs font-semibold rounded-full px-2 py-1 border-0 cursor-pointer focus:ring-2 focus:ring-green-500
                                                    ${order.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                                                        order.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                                                            order.status === 'Confirmed' ? 'bg-blue-100 text-blue-800' :
                                                                order.status === 'Shipped' ? 'bg-yellow-100 text-yellow-800' :
                                                                    'bg-gray-100 text-gray-800'}`}
                                            >
                                                <option value="Pending">Pending</option>
                                                <option value="Confirmed">Confirmed</option>
                                                <option value="Packed">Packed</option>
                                                <option value="Shipped">Shipped</option>
                                                <option value="Delivered">Delivered</option>
                                                <option value="Cancelled">Cancelled</option>
                                                <option value="Returned">Returned</option>
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminOrders;
